import { ExternalUrl, Fragment as KnowledgeFragment } from '@/shared/types/tryIt';

export type KnowledgeSource = {
  /** One document indexed in two data sources is two sources. */
  key: string;
  documentId: string;
  datasourceId: string;
  score?: number;
  isSelected: boolean;
  name?: string;
};

export type KnowledgeSources = {
  sources: KnowledgeSource[];
  retrieved: number;
  selected: number;
  topMatch?: KnowledgeSource;
};

type CitedUrl = ExternalUrl & { document_id: string };

// An entry without a document_id is a pseudo-source (Guidelines, User Info), not a document.
const isCited = (url: ExternalUrl): url is CitedUrl => !!url.document_id;

/** `score` is typed as required but arrives unvalidated. */
const readScore = (score: number) => (Number.isFinite(score) ? score : undefined);

const byScore = (a: KnowledgeSource, b: KnowledgeSource) =>
  (b.score ?? -Infinity) - (a.score ?? -Infinity) || a.key.localeCompare(b.key);

export const buildKnowledgeSources = (
  contents: KnowledgeFragment[] = [],
  externalUrls: ExternalUrl[] = []
): KnowledgeSources => {
  const cited = externalUrls.filter(isCited);
  const citedNames = new Map(cited.map((url) => [url.document_id, url.name]));

  const byDocument = new Map<string, KnowledgeSource>();

  const add = (documentId: string, datasourceId: string, score?: number) => {
    const key = `${documentId}-${datasourceId}`;
    const known = byDocument.get(key);
    // Fragments are per chunk, so one document can arrive several times — keep its best.
    if (known && (score ?? -Infinity) <= (known.score ?? -Infinity)) {
      return;
    }
    byDocument.set(key, {
      key,
      documentId,
      datasourceId,
      score,
      isSelected: citedNames.has(documentId),
      name: citedNames.get(documentId),
    });
  };

  contents.forEach((fragment) =>
    add(fragment.document_id, fragment.datasource_id, readScore(fragment.score))
  );

  // Only a document the retrieval missed earns its own row, data source or not.
  const retrievedDocuments = new Set([...byDocument.values()].map((source) => source.documentId));
  cited.forEach((url) => {
    if (!retrievedDocuments.has(url.document_id)) {
      add(url.document_id, url.datasource_id ?? '');
    }
  });

  const sources = [...byDocument.values()].sort(byScore);
  const selectedSources = sources.filter((source) => source.isSelected);

  return {
    sources,
    retrieved: sources.length,
    selected: selectedSources.length,
    topMatch: selectedSources[0],
  };
};
